import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import socket from '../services/socket';
import API_URL from '../config/api';

const AUCTION_TIMEOUT = 5000; // 5 sekund neaktivity pro ukončení aukce

const LiveAuction = ({ auction, onStatusChange }) => {
  const { currentUser } = useAuth();
  const [error, setError] = useState('');
  const [currentPrice, setCurrentPrice] = useState(auction.currentBid);
  const [customBidAmount, setCustomBidAmount] = useState(auction.currentBid + 1000);
  const [lastBidder, setLastBidder] = useState(auction.lastBidder);
  const [timeoutId, setTimeoutId] = useState(null);
  const [remainingTime, setRemainingTime] = useState(5);
  const [showResult, setShowResult] = useState(false);

  // Reset časovače při novém příhozu
  const resetTimeout = () => {
    if (timeoutId) clearTimeout(timeoutId);
    
    const newTimeoutId = setTimeout(() => {
      // Nejdřív zobrazíme výsledek
      setShowResult(true);

      // Pak emitujeme změnu stavu
      socket.emit('auctionStatusChanged', {
        auctionId: auction._id,
        newStatus: 'ended',
        winner: lastBidder,
        finalPrice: currentPrice
      });
    }, AUCTION_TIMEOUT);
    
    setTimeoutId(newTimeoutId);
    setRemainingTime(5);
  };

  // Odpočet zbývajícího času
  useEffect(() => {
    const timer = setInterval(() => {
      setRemainingTime(prev => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Socket listener pro příhozy
  useEffect(() => {
    socket.on('bidUpdated', (data) => {
      if (data.auctionId === auction._id) {
        setCurrentPrice(data.amount);
        setCustomBidAmount(data.amount + 1000);
        setLastBidder(data.userId);
        setShowResult(false); // Reset výsledku při novém příhozu
        resetTimeout();
      }
    });

    socket.on('auctionStatusChanged', (data) => {
      if (data.auctionId === auction._id && data.newStatus === 'ended') {
        setShowResult(true);
        onStatusChange('ended', {
          winner: data.winner,
          finalPrice: data.finalPrice
        });
      }
    });

    return () => {
      socket.off('bidUpdated');
      socket.off('auctionStatusChanged');
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [auction._id, timeoutId]);

  // Logika příhozů
  const handleBid = async (amount) => {
    if (!currentUser) {
      setError('Pro příhoz musíte být přihlášeni');
      return;
    }

    try {
      const token = await currentUser.getIdToken();
      
      const response = await fetch(`${API_URL}/api/bids`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          auctionId: auction._id,
          amount
        })
      });

      if (!response.ok) {
        throw new Error(`Server vrátil status ${response.status}`);
      }

      const bidData = await response.json();
      
      socket.emit('bidUpdated', {
        auctionId: auction._id,
        amount: bidData.amount,
        userId: currentUser.uid,
        createdAt: bidData.createdAt
      });

      setCurrentPrice(bidData.amount);
      setCustomBidAmount(bidData.amount + 1000);
      setLastBidder(currentUser.uid);
      resetTimeout();
    } catch (error) {
      setError(`Chyba při příhozu: ${error.message}`);
    }
  };

  // Načtení nejvyššího příhozu při startu
  useEffect(() => {
    const fetchHighestBid = async () => {
      try {
        const response = await fetch(`${API_URL}/api/bids/highest/${auction._id}`);
        if (!response.ok) {
          throw new Error(`Server vrátil status ${response.status}`);
        }
        
        const data = await response.json();
        if (data.amount) {
          setCurrentPrice(data.amount);
          setCustomBidAmount(data.amount + 1000);
          setLastBidder(data.userId);
        }
      } catch (error) {
        console.error('Chyba při načítání nejvyššího příhozu:', error);
        setError('Nepodařilo se načíst aktuální stav aukce');
      }
    };

    fetchHighestBid();
  }, [auction._id]);

  // Zobrazení výsledku
  if (showResult) {
    return (
      <div className="bg-white p-6 rounded-lg shadow">
        {lastBidder === currentUser?.uid ? (
          <div className="text-center">
            <div className="mb-4">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100">
                <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <h2 className="text-3xl font-bold text-green-600 mb-4">
              Gratulujeme!
            </h2>
            <p className="text-xl text-green-700 mb-2">
              Vyhráli jste tuto aukci
            </p>
            <p className="text-lg text-green-600">
              Konečná cena: {currentPrice.toLocaleString()} Kč
            </p>
          </div>
        ) : (
          <div className="text-center">
            <div className="mb-4">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100">
                <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            </div>
            <h2 className="text-3xl font-bold text-red-600 mb-4">
              Aukce skončila
            </h2>
            <p className="text-xl text-red-700 mb-2">
              Bohužel jste nevyhráli
            </p>
            <p className="text-lg text-red-600">
              Konečná cena: {currentPrice.toLocaleString()} Kč
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-gray-800 p-6 rounded-lg shadow-lg transition-all duration-300">
      <h2 className="text-2xl font-bold mb-4 text-white">Probíhající aukce</h2>
      
      <div className="relative mb-6">
        {/* Kruhový odpočet */}
        <div className="w-32 h-32 mx-auto relative">
          <svg className="w-full h-full transform -rotate-90">
            <circle
              cx="64"
              cy="64"
              r="60"
              fill="transparent"
              stroke={lastBidder === currentUser?.uid ? '#059669' : '#dc2626'}
              strokeWidth="8"
              strokeDasharray={`${(remainingTime / 5) * 377} 377`}
              className="transition-all duration-1000"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-3xl font-bold text-white">
              {remainingTime}s
            </div>
          </div>
        </div>
      </div>

      <div className="mb-6 bg-gray-700/50 p-4 rounded-lg">
        <p className="text-lg text-white">
          Aktuální cena: 
          <span className="text-green-400 font-bold ml-2">
            {currentPrice.toLocaleString()} Kč
          </span>
        </p>
        <p className={`text-sm mt-2 ${
          lastBidder === currentUser?.uid 
            ? 'text-green-400' 
            : 'text-red-400'
        }`}>
          {lastBidder === currentUser?.uid 
            ? '✓ Máte nejvyšší příhoz!' 
            : '× Někdo přihodil více než vy'}
        </p>
      </div>

      {error && (
        <div className="bg-red-900/50 border border-red-500 text-red-300 px-4 py-3 rounded mb-4 animate-shake">
          {error}
        </div>
      )}

      <div className="space-y-4">
        <button
          onClick={() => handleBid(currentPrice + 1000)}
          className="w-full bg-green-600 text-white py-3 px-4 rounded-lg
            hover:bg-green-700 transform hover:scale-105 transition-all duration-300"
        >
          Přihodit {(1000).toLocaleString()} Kč
        </button>

        <form onSubmit={(e) => {
          e.preventDefault();
          handleBid(customBidAmount);
        }} className="flex space-x-2">
          <input
            type="number"
            value={customBidAmount}
            onChange={(e) => setCustomBidAmount(Number(e.target.value))}
            min={currentPrice + 1000}
            step="1000"
            className="flex-1 px-4 py-3 bg-gray-700 text-white rounded-lg
              border border-gray-600 focus:ring-2 focus:ring-blue-500 
              focus:border-transparent transition-all duration-300"
            placeholder="Vlastní částka"
          />
          <button
            type="submit"
            className="bg-blue-600 text-white px-6 py-3 rounded-lg
              hover:bg-blue-700 transform hover:scale-105 
              transition-all duration-300 whitespace-nowrap"
          >
            Přihodit
          </button>
        </form>
      </div>
    </div>
  );
};

export default LiveAuction; 